import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { toCssPx } from "../../lib/appZoom";
import Checkbox from "./Checkbox";
import Pagination from "./Pagination";
import Skeleton from "./Skeleton";

const DEFAULT_WIDTH = 190;
const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 800;
const RESIZE_KEY_STEP = 16;

const getSortValue = (row, column) => {
  if (typeof column.accessor === "function") {
    return column.accessor(row);
  }
  return column.key ? row[column.key] : undefined;
};

const compareValues = (a, b) => {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "string" && typeof b === "string") return a.localeCompare(b);
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

const defaultRowKey = (row) => {
  if (!row) return null;
  return row.id ?? row.id_parceiro ?? row.parceiroId ?? row.codigo ?? row.key ?? null;
};

const renderCellContent = (column, row) => {
  if (typeof column.render === "function") {
    return column.render(row[column.key], row);
  }
  return String(row[column.key] ?? "-");
};

const getMobileFieldSpan = (column) => {
  const span = Number(column?.mobileSpan);
  return span === 1 ? 1 : 2;
};

// No card, a coluna pode ter uma versão compacta (ex.: só o nome do responsável, sem e-mail).
const renderMobileCellContent = (column, row) => {
  if (typeof column.mobileRender === "function") {
    return column.mobileRender(row[column.key], row);
  }
  return renderCellContent(column, row);
};

/**
 * Anatomia do card em telas pequenas, declarada por coluna com `mobileSlot`
 * (docs/design/PADROES_MOBILE.md):
 *   title    — o nome do registro, em destaque
 *   subtitle — dados de apoio numa linha só, separados por "·"
 *   badge    — selos de status, lado a lado
 *   meta     — pares rótulo/valor em duas colunas (padrão de quem não declarar)
 *   footer   — linha final separada (ex.: responsável)
 *   hidden   — não aparece no card
 * Tabela sem nenhum `mobileSlot` continua no card antigo, que lista coluna por coluna.
 */
const MOBILE_SLOTS = ["title", "subtitle", "badge", "meta", "footer"];


export default function DataTable({
  columns = [],
  data = [],
  actions = [],
  loading = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  enablePagination = true,
  emptyState = "Nenhum registro encontrado",
  className = "",
  selectable = false,
  onRowClick,
  onRowSelect,
  onSelectAll,
  onSelectAllMatching,
  selectedRows = [],
  allSelected = false,
  allMatchingSelected = false,
  rowKey = defaultRowKey,
  loadingRows = 5,
  pageSizeOptions = [20, 50, 100],
  selectAllMatchingLoading = false,
}) {
  // Arraste em andamento na divisória do cabeçalho; guardado para limpar os ouvintes ao desmontar.
  const resizeRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.key] = column.width || DEFAULT_WIDTH;
      return acc;
    }, {})
  );
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    setColumnWidths((prev) => {
      const nextWidths = { ...prev };
      columns.forEach((column) => {
        nextWidths[column.key] = nextWidths[column.key] || column.width || DEFAULT_WIDTH;
      });
      return nextWidths;
    });
  }, [columns]);

  useEffect(() => () => resizeRef.current?.cleanup(), []);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const column = columns.find((col) => col.key === sortConfig.key);
    if (!column) return data;
    const direction = sortConfig.direction === "desc" ? -1 : 1;
    return [...data].sort((a, b) => compareValues(getSortValue(a, column), getSortValue(b, column)) * direction);
  }, [data, columns, sortConfig]);

  // Todas as colunas ficam visíveis: o painel "Colunas" (esconder e largura por controle deslizante)
  // foi trocado em 2026-09-15 por arrastar a divisória do cabeçalho, a pedido do dono.
  const visibleColumnDefs = columns;

  const clampColumnWidth = (column, width) =>
    Math.min(column.maxWidth || MAX_COLUMN_WIDTH, Math.max(column.minWidth || MIN_COLUMN_WIDTH, Math.round(width)));

  const setColumnWidth = (column, width) =>
    setColumnWidths((prev) => ({ ...prev, [column.key]: clampColumnWidth(column, width) }));

  const startColumnResize = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current?.cleanup();
    const startX = event.clientX;
    const startWidth = columnWidths[column.key] ?? column.width ?? DEFAULT_WIDTH;
    // clientX vem em pixel real da tela e a largura da coluna vive dentro do zoom do app:
    // sem converter, a divisória andaria mais rápido que o cursor.
    const handleMove = (moveEvent) => setColumnWidth(column, startWidth + toCssPx(moveEvent.clientX - startX));
    const cleanup = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", cleanup);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", cleanup);
    // Mantém o cursor e evita selecionar texto enquanto arrasta para fora do cabeçalho.
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    resizeRef.current = { cleanup };
  };

  const handleResizeKeyDown = (event, column) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const current = columnWidths[column.key] ?? column.width ?? DEFAULT_WIDTH;
    setColumnWidth(column, current + (event.key === "ArrowRight" ? RESIZE_KEY_STEP : -RESIZE_KEY_STEP));
  };

  const totalWidth = useMemo(() => {
    let sum = visibleColumnDefs.reduce((acc, col) => acc + (columnWidths[col.key] ?? DEFAULT_WIDTH), 0);
    if (selectable) sum += 48;
    if (actions && actions.length > 0) sum += 128;
    return sum;
  }, [visibleColumnDefs, columnWidths, selectable, actions]);

  const orderedMobileColumns = useMemo(() => {
    const originalIndexMap = new Map(visibleColumnDefs.map((column, index) => [column.key, index]));
    return visibleColumnDefs
      .filter((column) => column.mobileHidden !== true)
      .sort((left, right) => {
        const leftOrder = Number.isFinite(left.mobileOrder) ? left.mobileOrder : Number.MAX_SAFE_INTEGER;
        const rightOrder = Number.isFinite(right.mobileOrder) ? right.mobileOrder : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return (originalIndexMap.get(left.key) ?? 0) - (originalIndexMap.get(right.key) ?? 0);
      });
  }, [visibleColumnDefs]);

  const primaryMobileColumn =
    orderedMobileColumns.find((column) => column.mobilePrimary) || orderedMobileColumns[0] || null;

  const usesMobileSlots = orderedMobileColumns.some((column) => column.mobileSlot);
  const mobileSlots = useMemo(() => {
    const slots = Object.fromEntries(MOBILE_SLOTS.map((slot) => [slot, []]));
    if (!usesMobileSlots) return slots;
    orderedMobileColumns.forEach((column) => {
      const slot = column.mobileSlot || (column.mobilePrimary ? "title" : "meta");
      if (slots[slot]) slots[slot].push(column);
    });
    return slots;
  }, [orderedMobileColumns, usesMobileSlots]);
  // Com a anatomia declarada, o card vale até o tablet (lg) e ganha duas colunas a partir de md.
  // Sem ela, mantém o corte antigo em md para não mudar tela que ainda não foi revisada.
  const cardsOnlyClass = usesMobileSlots ? "lg:hidden" : "md:hidden";
  const tableOnlyClass = usesMobileSlots ? "lg:block" : "md:block";
  const secondaryMobileColumns = primaryMobileColumn
    ? orderedMobileColumns.filter((column) => column.key !== primaryMobileColumn.key)
    : orderedMobileColumns;

  const handleSortToggle = (column) => {
    if (column.sortable === false) return;
    setSortConfig((current) => {
      if (current.key !== column.key) return { key: column.key, direction: "asc" };
      if (current.direction === "asc") return { key: column.key, direction: "desc" };
      return { key: null, direction: null };
    });
  };

  const paginationActive = enablePagination && pagination && onPageChange;
  const currentPageNumber = pagination?.page ?? 0;
  const totalPages = pagination?.totalPages ?? 0;
  const currentPageSize = Number(pagination?.size || data.length || pageSizeOptions[0] || 20);
  const totalElements = Number(pagination?.totalElements ?? data.length ?? 0);
  const totalSelectableCount = selectable ? totalElements : 0;
  // O tamanho atual sempre aparece no seletor, mesmo fora das opções padrão; antes o select
  // mostrava a primeira opção enquanto a página vinha com outro tamanho.
  const pageSizeChoices = pageSizeOptions.includes(currentPageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, currentPageSize].sort((a, b) => a - b);

  const handleRowClick = (event, row) => {
    if (!onRowClick) return;
    if (event.target.closest("button") || event.target.closest("input") || event.target.closest("a")) return;
    onRowClick(row);
  };

  const skeletonLineWidths = ["w-full", "w-3/4", "w-2/3", "w-1/2", "w-5/6"];

  return (
    <div className={cn("relative space-y-3", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-[12px] text-muted-foreground">Exibindo {data.length} registros</div>
          {selectable && sortedData.length > 0 && (
            <label className={cn("inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-muted-foreground", cardsOnlyClass)}>
              <Checkbox checked={allSelected} onCheckedChange={(checked) => onSelectAll?.(checked)} />
              <span>Selecionar página</span>
            </label>
          )}
        </div>
      </div>

      {selectable && onSelectAllMatching && allSelected && !allMatchingSelected && totalSelectableCount > sortedData.length && (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-muted-foreground">
            Todos os {sortedData.length} registros desta página estão selecionados.
          </span>
          <button
            type="button"
            onClick={() => onSelectAllMatching()}
            disabled={selectAllMatchingLoading}
            className={cn(
              "inline-flex items-center justify-center rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition",
              selectAllMatchingLoading
                ? "cursor-not-allowed border-border opacity-60"
                : "border-border bg-accent text-foreground hover:bg-accent/80"
            )}
          >
            {selectAllMatchingLoading
              ? "Selecionando..."
              : `Selecionar todos os ${totalSelectableCount}`}
          </button>
        </div>
      )}

      {selectable && allMatchingSelected && totalSelectableCount > 0 && (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-muted-foreground">
            Todos os {totalSelectableCount} registros filtrados estão selecionados.
          </span>
          <button
            type="button"
            onClick={() => onSelectAll?.(false)}
            className="inline-flex items-center justify-center rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition hover:border-foreground/30 hover:text-foreground"
          >
            Limpar seleção
          </button>
        </div>
      )}

      <div className={cn(usesMobileSlots ? "grid gap-3 md:grid-cols-2" : "space-y-3", cardsOnlyClass)}>
        {loading ? (
          Array.from({ length: loadingRows }).map((_, rowIndex) => (
            <div key={`mobile-skeleton-${rowIndex}`} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <div className="space-y-3">
                <Skeleton className={cn("h-5", skeletonLineWidths[rowIndex % skeletonLineWidths.length])} />
                <div className="grid grid-cols-1 gap-2">
                  <Skeleton className="h-14 w-full rounded-xl" />
                  <Skeleton className="h-14 w-full rounded-xl" />
                  <Skeleton className="h-14 w-full rounded-xl" />
                </div>
                {actions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-9 w-24 rounded-lg" />
                    <Skeleton className="h-9 w-24 rounded-lg" />
                  </div>
                )}
              </div>
            </div>
          ))
        ) : sortedData.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
            {emptyState}
          </div>
        ) : (
          sortedData.map((row, rowIndex) => {
            const resolvedRowKey = rowKey(row) ?? rowIndex;
            const isSelected = selectable && selectedRows.includes(resolvedRowKey);
            const visibleActions = actions.filter((action) => {
              const hidden = typeof action.hidden === "function" ? action.hidden(row) : Boolean(action.hidden);
              return !hidden;
            });

            if (usesMobileSlots) {
              return (
                <MobileSlotCard
                  key={`mobile-${resolvedRowKey}`}
                  row={row}
                  rowIndex={rowIndex}
                  slots={mobileSlots}
                  isSelected={isSelected}
                  selectable={selectable}
                  clickable={Boolean(onRowClick)}
                  onClick={(event) => handleRowClick(event, row)}
                  onSelect={(checked) => onRowSelect?.(row, resolvedRowKey, checked)}
                  actions={visibleActions}
                />
              );
            }

            return (
              <article
                key={`mobile-${resolvedRowKey}`}
                className={cn(
                  "rounded-2xl border border-border/80 bg-card p-4 shadow-sm transition-all dark:bg-card/95 dark:backdrop-blur-sm",
                  isSelected
                    ? "bg-accent outline outline-1 outline-foreground/20 -outline-offset-1"
                    : "",
                  onRowClick ? "cursor-pointer active:bg-accent/20" : ""
                )}
                onClick={(event) => handleRowClick(event, row)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {primaryMobileColumn ? (
                      <>
                        <div className="text-xs text-muted-foreground">
                          {primaryMobileColumn.header}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {renderCellContent(primaryMobileColumn, row)}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm font-semibold text-foreground">Registro {rowIndex + 1}</div>
                    )}
                  </div>
                  {selectable && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => onRowSelect?.(row, resolvedRowKey, checked)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  )}
                </div>

                {secondaryMobileColumns.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-border/40 pt-3">
                    {secondaryMobileColumns.map((column) => (
                      <div
                        key={`mobile-field-${column.key}-${resolvedRowKey}`}
                        className={cn(
                          "min-w-0 space-y-1",
                          getMobileFieldSpan(column) === 1 ? "col-span-1" : "col-span-2"
                        )}
                      >
                        <div className="text-xs text-muted-foreground">
                          {column.mobileLabel || column.header}
                        </div>
                        <div className="text-sm text-foreground break-words">
                          {renderCellContent(column, row)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {visibleActions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-border/40 pt-3">
                    {visibleActions.map((action) => {
                      const disabled = typeof action.disabled === "function" ? action.disabled(row) : Boolean(action.disabled);

                      return (
                        <button
                          key={`mobile-action-${resolvedRowKey}-${action.title}`}
                          type="button"
                          title={action.title}
                          disabled={disabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!disabled) action.onClick(row);
                          }}
                          className={cn(
                            "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border/70 bg-background/80 px-3 py-2 text-xs font-medium text-foreground transition-all",
                            disabled
                              ? "cursor-not-allowed opacity-45"
                              : "hover:border-foreground/30 hover:text-foreground hover:shadow-sm active:scale-[0.98]"
                          )}
                        >
                          <action.icon className="h-4 w-4" />
                          <span>{action.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      <div className={cn("relative hidden overflow-visible rounded-2xl border border-border bg-card", tableOnlyClass)}>
        <div className="overflow-auto">
          <table 
            className="w-full table-fixed text-sm"
            style={{ minWidth: `${totalWidth}px` }}
          >
            <thead className="border-b border-border text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                {selectable && (
                  <th className="w-12 px-3 py-3 text-center">
                    <Checkbox checked={allSelected} onCheckedChange={(checked) => onSelectAll?.(checked)} />
                  </th>
                )}
                {visibleColumnDefs.map((column) => (
                  <th
                    key={column.key}
                    className={cn("relative px-3 py-3 text-left", column.align === "center" ? "text-center" : "text-left")}
                    style={{
                      width: columnWidths[column.key] ?? column.minWidth ?? DEFAULT_WIDTH,
                      minWidth: column.minWidth ?? MIN_COLUMN_WIDTH,
                    }}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-1 text-[11px] font-semibold text-muted-foreground"
                      onClick={() => handleSortToggle(column)}
                    >
                      <span className="truncate text-[13px] leading-snug">{column.header}</span>
                      {column.sortable !== false && <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                    {/* Divisória arrastável: ajusta a largura da coluna. Setas do teclado ajustam de 16 em
                        16px; duplo clique volta à largura padrão da coluna. */}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Ajustar largura da coluna ${column.header}`}
                      aria-valuenow={columnWidths[column.key] ?? DEFAULT_WIDTH}
                      tabIndex={0}
                      title="Arraste para ajustar a largura"
                      onPointerDown={(event) => startColumnResize(event, column)}
                      onDoubleClick={() => setColumnWidth(column, column.width || DEFAULT_WIDTH)}
                      onKeyDown={(event) => handleResizeKeyDown(event, column)}
                      onClick={(event) => event.stopPropagation()}
                      className="group absolute -right-1.5 top-0 z-10 flex h-full w-3 cursor-col-resize touch-none select-none justify-center focus-visible:outline-none"
                    >
                      <span
                        className="h-full w-px bg-border transition-colors group-hover:w-0.5 group-hover:bg-foreground/50 group-focus-visible:w-0.5 group-focus-visible:bg-foreground/50"
                        aria-hidden="true"
                      />
                    </span>
                  </th>
                ))}
                {actions.length > 0 && (
                  <th className="w-32 px-3 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Ações
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-background text-sm">
              {loading ? (
                Array.from({ length: loadingRows }).map((_, rowIndex) => (
                  <tr key={`skeleton-row-${rowIndex}`} className="border-b border-border last:border-none">
                    {selectable && (
                      <td className="px-3 py-3 text-center">
                        <Skeleton className="mx-auto h-4 w-4 rounded" />
                      </td>
                    )}
                    {visibleColumnDefs.map((column, colIndex) => (
                      <td
                        key={`skeleton-col-${column.key}-${rowIndex}`}
                        className={cn("px-3 py-3 align-top", column.align === "center" ? "text-center" : "text-left")}
                        style={{
                          width: columnWidths[column.key] ?? column.minWidth ?? DEFAULT_WIDTH,
                        }}
                      >
                        <div className={cn(column.align === "center" ? "mx-auto" : "") }>
                          <Skeleton className={cn("h-4", skeletonLineWidths[(rowIndex + colIndex) % skeletonLineWidths.length])} />
                        </div>
                      </td>
                    ))}
                    {actions.length > 0 && (
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-8 w-8 rounded-full" />
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnDefs.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}>
                    <div className="flex items-center justify-center px-6 py-10 text-sm text-muted-foreground">
                      {emptyState}
                    </div>
                  </td>
                </tr>
              ) : (
                sortedData.map((row, rowIndex) => {
                  const resolvedRowKey = rowKey(row) ?? rowIndex;
                  const isSelected = selectable && selectedRows.includes(resolvedRowKey);
                  return (
                    <tr
                      key={resolvedRowKey}
                      className={cn(
                        "border-b border-border last:border-none transition-colors",
                        isSelected
                          ? "bg-accent text-foreground outline outline-1 outline-foreground/20 -outline-offset-1"
                          : rowIndex % 2 === 0
                            ? "bg-background"
                            : "bg-muted/10",
                        onRowClick ? "cursor-pointer hover:bg-accent/20" : ""
                      )}
                      onClick={(event) => handleRowClick(event, row)}
                    >
                      {selectable && (
                        <td className="px-3 py-3 text-center">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => onRowSelect?.(row, resolvedRowKey, checked)}
                            onClick={(event) => event.stopPropagation()}
                          />
                        </td>
                      )}
                      {visibleColumnDefs.map((column) => (
                        <td
                          key={`${column.key}-${resolvedRowKey}`}
                          className={cn("px-3 py-3 align-top", column.align === "center" ? "text-center" : "text-left")}
                          style={{
                            width: columnWidths[column.key] ?? column.minWidth ?? DEFAULT_WIDTH,
                          }}
                        >
                          {renderCellContent(column, row)}
                        </td>
                      ))}
                      {actions.length > 0 && (
                        <td className="flex items-center justify-center gap-2 px-2 py-3">
                          <div className="flex flex-wrap gap-2">
                            {actions.map((action) => {
                              const hidden = typeof action.hidden === "function" ? action.hidden(row) : Boolean(action.hidden);
                              if (hidden) return null;
                              const disabled = typeof action.disabled === "function" ? action.disabled(row) : Boolean(action.disabled);

                              return (
                                <button
                                  key={action.title}
                                  type="button"
                                  title={action.title}
                                  disabled={disabled}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!disabled) action.onClick(row);
                                  }}
                                  className={cn(
                                    "flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition",
                                    disabled
                                      ? "cursor-not-allowed opacity-45"
                                      : "hover:border-foreground/30 hover:text-foreground"
                                  )}
                                >
                                  <action.icon className="h-4 w-4" />
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {paginationActive && (
        <Pagination
          page={currentPageNumber + 1}
          pageSize={currentPageSize}
          totalItems={totalElements}
          totalPages={Math.max(totalPages, 1)}
          itemLabel="registros"
          disabled={loading}
          bordered={false}
          hideWhenSinglePage={false}
          onPageChange={(nextPage) => onPageChange(nextPage - 1)}
          leading={
            onPageSizeChange ? (
              <label className="flex items-center gap-2">
                <span>Por página</span>
                <select
                  value={String(currentPageSize)}
                  onChange={(event) => onPageSizeChange(Number(event.target.value))}
                  disabled={loading}
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {pageSizeChoices.map((option) => (
                    <option key={`page-size-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            ) : null
          }
        />
      )}
    </div>
  );
}

function MobileSlotCard({ row, rowIndex, slots, isSelected, selectable, clickable, onClick, onSelect, actions }) {
  const subtitleParts = slots.subtitle
    .map((column) => ({ key: column.key, content: renderMobileCellContent(column, row) }))
    .filter((part) => part.content !== null && part.content !== undefined && part.content !== "" && part.content !== "-");

  return (
    <article
      className={cn(
        "flex min-w-0 flex-col rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors",
        isSelected ? "bg-accent outline outline-1 outline-foreground/20 -outline-offset-1" : "",
        clickable ? "cursor-pointer active:bg-accent/60" : ""
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="break-words text-[15px] font-semibold leading-snug text-foreground">
            {slots.title.length
              ? slots.title.map((column) => (
                <React.Fragment key={column.key}>{renderMobileCellContent(column, row)}</React.Fragment>
              ))
              : `Registro ${rowIndex + 1}`}
          </div>
          {subtitleParts.length ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              {subtitleParts.map((part, index) => (
                <React.Fragment key={part.key}>
                  {index > 0 ? <span aria-hidden="true">·</span> : null}
                  <span className="min-w-0 break-words">{part.content}</span>
                </React.Fragment>
              ))}
            </div>
          ) : null}
        </div>
        {selectable ? (
          // Área de toque de 44px em volta do checkbox; tocar nela não abre o registro.
          <label
            className="-m-3 grid shrink-0 cursor-pointer place-items-center p-3"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox checked={isSelected} onCheckedChange={onSelect} className="h-5 w-5" aria-label="Selecionar registro" />
          </label>
        ) : clickable ? (
          <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : null}
      </div>

      {slots.badge.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {slots.badge.map((column) => (
            <React.Fragment key={column.key}>{renderMobileCellContent(column, row)}</React.Fragment>
          ))}
        </div>
      ) : null}

      {slots.meta.length ? (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {slots.meta.map((column) => (
            <div key={column.key} className={cn("min-w-0", getMobileFieldSpan(column) === 1 ? "col-span-1" : "col-span-2")}>
              <dt className="text-xs text-muted-foreground">{column.mobileLabel || column.header}</dt>
              <dd className="mt-0.5 break-words text-sm text-foreground">{renderMobileCellContent(column, row)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {slots.footer.length ? (
        // mt-auto alinha o rodapé no fundo quando os cards ficam lado a lado no tablet.
        <div className="mt-auto pt-3">
          <div className="space-y-2 border-t border-border/60 pt-3">
            {slots.footer.map((column) => (
              <div key={column.key} className="min-w-0 text-sm text-foreground">{renderMobileCellContent(column, row)}</div>
            ))}
          </div>
        </div>
      ) : null}

      {actions.length ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-border/60 pt-3">
          {actions.map((action) => {
            const disabled = typeof action.disabled === "function" ? action.disabled(row) : Boolean(action.disabled);
            return (
              <button
                key={action.title}
                type="button"
                title={action.title}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!disabled) action.onClick(row);
                }}
                className={cn(
                  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 text-xs font-medium text-foreground transition",
                  disabled ? "cursor-not-allowed opacity-45" : "hover:border-foreground/30 active:scale-[0.98]"
                )}
              >
                <action.icon className="h-4 w-4" />
                <span>{action.title}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </article>
  );
}
